import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type {
  PlannerHeatView,
  SearchSelectOption,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import { useFlightPlannerPort } from '../flight_planner_port.js';
import { airportNaming } from '../ui/airport_naming.js';
import { useFlightAirportHistoryPort } from '../airport_history_port.js';
import {
  airportLabelOf,
  airportOptionOf,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import {
  PlannerForm,
  useRecentAirports,
  type SummaryPillTarget,
  type FlightSearchParams,
} from '@voll-intelligence/intelligence-ui-kit';
import '@voll-intelligence/intelligence-ui-kit/flight-explorer.css';
import { flightPlannerCopy } from '../ui/flight_planner_copy.js';

/**
 * O planner inline da pill DO EXPLORER (`bandAsPill`) — o irmão do
 * `BookingPlannerOverlay` do web-chat, com a MESMA gramática (#2129/#2130):
 * painel ancorado sob a barra do takeover, scrim escurecendo a lista
 * preservada atrás, o `PlannerForm` aberto semeado com a busca corrente e o
 * `Cancelar` da edição. Cancelar/Escape/clique-fora recolhem SEM tocar em nada;
 * quem re-busca é exclusivamente o submit — e ele sai pelos MESMOS sinks da
 * antiga faixa (`onSearch`/`onEditLegs`), então o replanejar do drawer é o
 * replanejar que a banda fazia.
 *
 * Por que uma casca própria e não o `BookingPlannerOverlay`: a geometria é
 * outra (aqui o ancestral é o próprio `.fex`, não a `.chat-main` do shell) e os
 * seams são os do KIT (`GenUiConfig.flightHeat` + port `flightPlanner`), não o
 * TanStack Query do web-chat — o formulário e os contratos de foco/Escape são
 * os mesmos.
 */

/** Mês 'YYYY-MM' por partes LOCAIS (toISOString viraria UTC e, à noite BRT,
 *  pularia o mês) — a mesma régua do `currentMonth` da faixa. */
function currentMonth(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Âncora da janela do calendário: o MÊS DA VIAGEM (nunca antes do corrente) —
 *  ancorar em hoje abriria a grade longe das datas que a pessoa veio editar. */
function seedMonth(departDate: string | undefined): string {
  const current = currentMonth();
  const depart = departDate?.slice(0, 7);
  return depart && depart > current ? depart : current;
}

/** Fire-and-forget vazio quando não há seam de calor — o mesmo degrade da faixa. */
const NO_HEAT_FETCH = () => {};

/** Onde o foco NASCE por segmento clicado (contrato da #2129). Seletores dos
 *  primitivos do próprio kit; `edit` não força campo: foca o painel. O alvo de
 *  `dates` segue sendo a célula roving da grade — desde o #2136 ela mora num
 *  popover, que o form já monta ABERTO quando o host passa `initialDatesOpen`.
 *  `origin`/`destination` (pill binária, #2136) focam CADA UM o próprio campo
 *  via `data-endpoint` do `RouteField`; `route` (pill do multi, cadeia única)
 *  continua caindo no primeiro combobox da fileira. */
const FOCUS_SELECTOR: Record<SummaryPillTarget, string> = {
  trip_type: '[role="tab"][aria-selected="true"]',
  traveler: '.fpl-field-who button',
  origin: '[data-endpoint="origin"] [data-place-field-trigger]',
  destination: '[data-endpoint="destination"] [data-place-field-trigger]',
  route: '[data-place-field-trigger]',
  dates: 'button[data-date][tabindex="0"]',
  edit: '',
};

function focusTarget(panel: HTMLElement, target: SummaryPillTarget): void {
  const selector = FOCUS_SELECTOR[target];
  const scoped = selector ? panel.querySelector<HTMLElement>(selector) : null;
  // `dates`: a célula roving mora FORA do painel (#2174) — o `PlannerForm` recebe
  // `floatDatesPopover` aqui embaixo, então o popover de datas sai por portal pro
  // `document.body` pra escapar do `overflow-y: auto` do `.fxo-panel`. Só ESTE
  // alvo pode estar fora do painel — os demais seletores continuam estritos a
  // ele, sem risco de pegar um elemento de outra parte da página.
  const el = scoped ?? (target === 'dates' ? document.querySelector<HTMLElement>(selector) : null);
  (el ?? panel).focus();
  // O campo de praça é uma PÍLULA de popover (Base UI), e foco não abre popover
  // — o clique abre. O gesto que trouxe a pessoa até aqui ("origem"/"destino"
  // na pill, #2129) era um pedido de campo aberto, não de campo apontado.
  if (el?.hasAttribute('data-place-field-trigger')) el.click();
}

export function ExplorerPlannerOverlay({
  params,
  legs,
  target,
  onClose,
  onSearch,
  onEditLegs,
}: Readonly<{
  /** O pedido VIGENTE da perna em cena — a mesma semente da antiga faixa. */
  params: FlightSearchParams;
  /** Roteiro INTEIRO, só no multi-trecho (o form abre no modo multi). */
  legs?: { origin: string; destination: string; departDate: string }[];
  target: SummaryPillTarget;
  onClose: () => void;
  onSearch: (params: FlightSearchParams) => void;
  onEditLegs: (legs: { origin: string; destination: string; departDate: string }[]) => void;
}>) {
  const { t, locale } = useGenUiI18n();
  // Os textos do form no locale da conversa: memo por locale, uma identidade só.
  const plannerCopy = useMemo(() => flightPlannerCopy(locale), [locale]);
  const naming = useMemo(() => airportNaming(locale), [locale]);
  const { flightHeat, ports } = useGenUiConfig();
  const travelers = ports?.travelers;
  const plannerPort = useFlightPlannerPort();
  const panelRef = useRef<HTMLDivElement>(null);

  const searchAirports = useCallback(
    (term: string): Promise<SearchSelectOption[]> =>
      plannerPort
        ? plannerPort.searchAirports(term).then((as) => as.map((a) => airportOptionOf(a, naming)))
        : Promise.resolve([]),
    [plannerPort, naming],
  );
  // O histórico do viajante no DE/PARA, pelo port da conversa (o form o recebe por prop).
  const recentAirports = useRecentAirports(useFlightAirportHistoryPort(), naming);

  // O retrato do mount — a MESMA tradução da SearchBand (a forma é derivada:
  // `returnDate` decide, roteiro decide o multi).
  const initial = useMemo(
    () => ({
      route: { origin: params.origin, destination: params.destination },
      ...(legs && legs.length > 1
        ? {
            tripType: 'multi_leg' as const,
            legs: legs.map((l) => ({
              origin: l.origin,
              destination: l.destination,
              date: l.departDate,
            })),
          }
        : {
            tripType: params.returnDate ? ('round_trip' as const) : ('one_way' as const),
            departDate: params.departureDate,
            ...(params.returnDate ? { returnDate: params.returnDate } : {}),
          }),
    }),
    [params.origin, params.destination, params.departureDate, params.returnDate, legs],
  );

  // Sem seam de calor a vista é só a moldura — grade neutra e clicável, o mesmo
  // degrade da faixa. A janela ancora no MÊS DA VIAGEM (#2129).
  const fallbackHeat = useMemo(
    (): PlannerHeatView => ({
      route: { origin: params.origin, destination: params.destination },
      window: { fromMonth: seedMonth(params.departureDate), count: 3 },
    }),
    [params.origin, params.destination, params.departureDate],
  );

  // Escape recolhe (descarte, nunca envio) — listener no DOCUMENT, não no
  // painel: o foco não é garantidamente de dentro dele. (Até a #2330 havia uma
  // causa concreta: o shimmer do heat trocava a célula focada por um `<div>`
  // aria-hidden e o foco caía no body. Hoje o dia sobrevive à leitura, mas o
  // listener segue no document — combobox, popover e o próprio pill também
  // tiram o foco do painel.)
  // `defaultPrevented` respeita os popovers do form que consomem o próprio
  // Escape (combobox com stopPropagation no input) — o primeiro Esc fecha o
  // popover, o segundo chega aqui. A casca do drawer DEFERE enquanto o overlay
  // está em cena (`.fxo` no painel dela), então este é o único fecho do Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Foco de volta ao segmento da pill ao desmontar (contrato `data-seg`).
  // Microtask: no mesmo commit a pill sai de `data-hidden`, e focar antes de
  // o atributo cair seria focar um nó invisível, que o browser ignora.
  useEffect(() => {
    return () => {
      queueMicrotask(() => {
        document
          .querySelector<HTMLElement>(`[data-slot="summary-pill"] [data-seg="${target}"]`)
          ?.focus();
      });
    };
  }, [target]);

  // Foco ENTRA no segmento clicado — e é RE-ENTREGUE quando o shimmer do heat
  // troca a grade por placeholders e o foco cai no body (as células somem do
  // DOM enquanto `heatLoading`). Só resgata do body, nunca rouba de onde a
  // pessoa já pôs o foco — a mesma régua do overlay do shell (#2129).
  const heatLoading = flightHeat?.loading ?? false;
  const focusDelivered = useRef(false);
  useEffect(() => {
    if (heatLoading) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (focusDelivered.current && document.activeElement !== document.body) return;
    focusDelivered.current = true;
    focusTarget(panel, target);
  }, [heatLoading, target]);

  return (
    <div className="fxo" role="dialog" aria-modal="true" aria-label={t('flightExplorer.pillEdit')}>
      <div className="fxo-scrim" onClick={onClose} role="presentation" />
      <div className="fxo-panel genui2" ref={panelRef} tabIndex={-1}>
        <div className="fxo-inner">
          <PlannerForm
            initial={initial}
            heat={flightHeat?.heat ?? fallbackHeat}
            heatLoading={flightHeat?.loading ?? false}
            autoFetchHeat={Boolean(flightHeat)}
            fetchHeat={flightHeat?.fetch ?? NO_HEAT_FETCH}
            searchAirports={searchAirports}
            recentAirports={recentAirports}
            cityOfAirport={naming.cityByAirport}
            airportLabel={(iata) => airportLabelOf(iata, naming)}
            travelerDirectory={travelers}
            copy={plannerCopy}
            locale={locale}
            // O pedido do explorer não carrega roster nem nomes — QUEM aqui
            // seria convite morto (a mesma régua da antiga faixa).
            hideWho
            // O segmento de DATAS pediu calendário: o popover do QUANDO nasce
            // aberto (#2136), e o foco cai na célula roving de sempre.
            initialDatesOpen={target === 'dates'}
            // O `.fxo-panel` tem `max-height` + `overflow-y: auto` (`explorer.css`)
            // — sem o portal o popover de datas nascia CORTADO no pé do painel em
            // vez de flutuar por cima dele (#2174).
            floatDatesPopover
            onCancel={onClose}
            onSubmit={(sel) => {
              if (sel.action === 'search_multi') {
                onEditLegs(
                  sel.legs.map((l) => ({
                    origin: l.origin,
                    destination: l.destination,
                    departDate: l.date,
                  })),
                );
                onClose();
                return;
              }
              // O sujeito da política e a cabine são do PEDIDO VIGENTE — a
              // mesma régua da faixa (`SearchBand.onSubmit`).
              const { policyTravelerId } = params;
              onSearch({
                origin: sel.origin,
                destination: sel.destination,
                departureDate: sel.outboundDate,
                ...(sel.returnDate ? { returnDate: sel.returnDate } : {}),
                ...(params.cabin ? { cabin: params.cabin } : {}),
                ...(policyTravelerId ? { policyTravelerId } : {}),
              });
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
}
